"use client";

import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, ChevronDown, Clock } from 'lucide-react';
import { submitEnquiry } from '../../utils/formSubmission';
import CallButton from "@/components/CallButton";

const contactInfoItems = [
    {
        icon: MapPin,
        label: "Headquarters",
        value: "Emsys Solution Pvt. Ltd.,\nOpp. Vasant Market, Vatsalya, Vrundavan Colony,\nCanada Corner, Nashik, Maharashtra 422002",
    },
    {
  icon: Phone,
  label: "Call Us",
  // Injected your CallButton right here
  value: (
    <CallButton 
      href="tel:+919011060932" 
      location="contact" 
      className="hover:text-[#004d99] transition-colors font-medium"
    >
      +91 9011060932
    </CallButton>
  ),
  sub: "Mon – Sat, 10am – 7pm",
},
    {
        icon: Mail,
        label: "Email Us",
        value: "sales@emsys.co.in",
        sub: "Response within 24 hours",
    },
    {
        icon: Clock,
        label: "Business Hours",
        value: "Mon – Sat: 10:00 AM – 7:00 PM",
        sub: "Indian Standard Time (IST)",
    },
];

const ContactForm = () => {
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(e.target);
        const data = {
            name: formData.get('name'),
            email: formData.get('email'),
            phone: formData.get('phone'),
            subject: formData.get('subject') || "Contact Form Submission",
            message: formData.get('message'),
        };

        try {
            await submitEnquiry(data);
            setSubmitted(true);
            e.target.reset();
            setTimeout(() => setSubmitted(false), 6000);
        } catch (error) {
            console.error("Submission failed", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="py-16 md:py-24 bg-white">
            <div className="container mx-auto px-6 md:px-12 lg:px-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

                    {/* LEFT: Contact Info */}
                    <div className="space-y-10">
                        <div className="space-y-4">
                            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter leading-tight">
                                Ready to fuel <span className="text-brand">your growth?</span>
                            </h2>
                            <p className="text-slate-800 text-lg leading-relaxed">
                                Reach out to our automotive experts today for a personalized demo of our DMS ecosystem.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {contactInfoItems.map((info, i) => (
                                <div key={i} className="flex gap-5 items-start group">
                                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-brand shadow-sm group-hover:bg-brand group-hover:text-white transition-all duration-300">
                                        <info.icon size={22} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900">{info.label}</h3>
                                        <p className="text-slate-800 font-medium whitespace-pre-line">{info.value}</p>
                                        {info.sub && <p className="text-slate-400 text-xs font-bold tracking-tight">{info.sub}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Map Embed */}
                        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-md h-52">
                            <iframe
                                title="Emsys Nashik Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d119968.66834988624!2d73.67516!3d19.9974!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bddeba004c3cf27%3A0x6c2c64f7bb87c95f!2sNashik%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1680000000000"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>

                    {/* RIGHT: Form */}
                    <div className="relative">
                        <div className="bg-white p-8 md:p-10 rounded-[2.5rem] border border-brand/20 shadow-2xl shadow-slate-200/60">
                            {submitted ? (
                                <div className="py-20 flex flex-col items-center text-center space-y-5">
                                    <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center animate-bounce">
                                        <CheckCircle size={40} />
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-900">Message Sent!</h3>
                                    <p className="text-slate-800 font-medium max-w-xs">
                                        Thank you for reaching out. Our team will get back to you within 24 hours.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900 mb-1">Send Us a Message</h3>
                                        <p className="text-slate-400 text-sm">Fill in the details and we'll be in touch shortly.</p>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="form-name" className="text-[10px] font-black tracking-[0.15em] text-slate-400 ml-2">Full Name *</label>
                                        <input id="form-name" name="name" required type="text" placeholder="John Doe"
                                            className="w-full px-5 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold placeholder:text-slate-300" />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-1.5">
                                            <label htmlFor="form-email" className="text-[10px] font-black tracking-[0.15em] text-slate-400 ml-2">Email Address *</label>
                                            <input id="form-email" name="email" required type="email" placeholder="john@dealership.com"
                                                className="w-full px-5 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold placeholder:text-slate-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label htmlFor="form-phone" className="text-[10px] font-black tracking-[0.15em] text-slate-400 ml-2">Phone Number *</label>
                                            <input id="form-phone" name="phone" required type="tel" placeholder="+91 00000 00000"
                                                className="w-full px-5 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold placeholder:text-slate-300" />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5 relative">
                                        <label htmlFor="form-subject" className="text-[10px] font-black tracking-[0.15em] text-slate-400 ml-2">Interested In</label>
                                        <div className="relative">
                                            <select id="form-subject" name="subject" className="w-full px-5 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold appearance-none cursor-pointer">
                                                <option value="">Select a product...</option>
                                                <option>Autonet DMS</option>
                                                <option>Autonext Cloud DMS</option>
                                                <option>Body Shop Management</option>
                                                <option>MyPaper</option>
                                                <option>Custom Solution</option>
                                            </select>
                                            <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="form-message" className="text-[10px] font-black tracking-[0.15em] text-slate-400 ml-2">Your Message</label>
                                        <textarea id="form-message" name="message" rows="4" placeholder="Tell us about your dealership requirements..."
                                            className="w-full px-5 py-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand/5 transition-all outline-none text-slate-900 font-bold resize-none placeholder:text-slate-300" />
                                    </div>

                                    <button disabled={isSubmitting} type="submit" className="w-full py-5 bg-brand text-white rounded-xl font-black text-lg flex items-center justify-center gap-3 hover:bg-slate-900 hover:shadow-xl hover:shadow-brand/20 transition-all active:scale-[0.97] disabled:opacity-50">
                                        {isSubmitting ? 'Sending...' : 'Send Message'} <Send size={20} strokeWidth={2.5} />
                                    </button>
                                </form>
                            )}
                        </div>
                        <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-brand/5 rounded-full blur-3xl -z-10" />
                    </div>

                </div>
            </div>
        </section>
    );
};

export default ContactForm;
